import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'en' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Traductions
const translations = {
  en: {
    // Hero Section
    'hero.title': "Hi, I'm Nadir",
    'hero.subtitle': 'Web Developer & AI Enthusiast',
    'hero.description': 'I create beautiful, functional web experiences using React, Node.js, and cutting-edge technologies.',
    
    // Navigation
    'nav.about': 'About',
    'nav.projects': 'Projects',
    'nav.tools': 'Tools',
    'nav.resources': 'Resources',
    'nav.contact': 'Contact',
    
    // About Section
    'about.title': 'About Me',
    'about.subtitle': 'Passionate developer creating digital experiences',
    'about.description': 'I\'m a creative web developer with a passion for building innovative solutions. With experience in React, Flutter, Node.js, and AI/ML, I love turning ideas into reality through clean, efficient code.',
    'about.profile.name': 'Nadir',
    'about.profile.title': 'Web Developer & AI Enthusiast',
    'about.profile.description': 'Passionate developer with a creative artistic heart. I love colors, slick designs, and original well-organized UX inspired by video games. I specialize in creating beautiful, functional web experiences using modern technologies like React, Node.js, and exploring the exciting world of AI and machine learning.',
    'about.stats.experience': 'Months Experience',
    'about.stats.technologies': 'Technologies',
    'about.stats.projects': 'Major Project',
    'about.stats.creativity': 'Creativity',
    'about.skills.title': 'Skills & Technologies',
    'about.experience.title': 'Professional Experience',
    'about.experience.systra.position': 'Web Developer Intern',
    'about.experience.systra.company': 'SYSTRA',
    'about.experience.systra.description': 'Developed a web module integrated into the company\'s proprietary ERP system using TypeScript, React, and Node.js with PostgreSQL database.',
    'about.experience.systra.location': 'Paris, France',
    'about.skills.level.beginner': 'Beginner',
    'about.skills.level.intermediate': 'Intermediate',
    'about.skills.level.advanced': 'Advanced',
    'about.skills.level.expert': 'Expert',
    
    // Projects Section
    'projects.title': 'Featured Projects',
    'projects.subtitle': 'Showcasing my latest work and innovations',
    'projects.alfred.title': 'Alfred - Cooking Assistant',
    'projects.alfred.description': 'An intelligent cooking assistant that helps you create amazing meals with the ingredients you have at home.',
    'projects.alfred.intro': 'No more "what\'s for dinner tonight?" and forgotten groceries! Alfred transforms your kitchen into a space where cooking becomes a simple and intuitive pleasure.',
    'projects.alfred.features.waste.title': 'End food waste',
    'projects.alfred.features.waste.text': 'Alfred monitors your food, alerts you before expiration and suggests recipes to use what you have',
    'projects.alfred.features.stress.title': 'Cook stress-free',
    'projects.alfred.features.stress.text': 'Hands-free voice guidance, automatic timers and personalized advice according to your level',
    'projects.alfred.features.discover.title': 'Discover new dishes',
    'projects.alfred.features.discover.text': 'Smart recommendations based on your tastes, dietary restrictions and what\'s lying around in your fridge',
    'projects.alfred.features.time.title': 'Save time',
    'projects.alfred.features.time.text': 'Scan your groceries, plan your meals and let Alfred organize your cooking week',
    'projects.alfred.features.connected.title': 'A connected kitchen',
    'projects.alfred.features.connected.text': 'Control everything by voice while you cook, without ever touching your phone',
    'projects.alfred.objective.title': 'The goal',
    'projects.alfred.objective.text': 'Make cooking accessible to everyone, reduce daily meal stress and help you rediscover the joy of cooking, whether you\'re a beginner or an experienced chef.',
    'projects.meta.started': 'Started',
    'projects.meta.status.completed': 'Completed',
    'projects.meta.status.in-progress': 'In Progress',
    'projects.meta.status.planned': 'Planned',
    'projects.action.live-demo': 'Live Demo',
    'projects.action.view-code': 'View Code',
    'projects.action.app-store': 'App Store',
    'projects.action.google-play': 'Google Play',
    'projects.tech.expand': 'Show more',
    'projects.tech.collapse': 'Show less',
    'projects.tech.others': 'others',
    'projects.badge.soon': 'Soon',
    'projects.portfolio.title': 'Portfolio Swiss Knife',
    'projects.portfolio.description': 'Modular portfolio with integrated tools and custom design system.',
    'projects.systra.title': 'SYSTRA ERP Module',
    'projects.systra.description': 'Web module integrated into SYSTRA\'s proprietary ERP system for infrastructure project management.',
    
    // Tools Section
    'tools.title': 'Swiss Knife Tools',
    'tools.subtitle': 'Interactive tools and utilities',
    'tools.recipe.title': 'Recipe Generator',
    'tools.recipe.description': 'Generate delicious recipes from your available ingredients',
    'tools.redthread.title': 'Red Thread',
    'tools.redthread.description': 'Movie recommendation engine based on your preferences',
    'tools.comingsoon.title': 'Coming Soon',
    'tools.comingsoon.description': 'This tool is currently under development',
    'tools.comingsoon.feature.development': 'In Development',
    'tools.comingsoon.feature.development.desc': 'This tool is currently being developed',
    'tools.comingsoon.feature.available': 'Soon Available',
    'tools.comingsoon.feature.available.desc': 'Personalized movie recommendations',
    
    // Resources Section
    'resources.title': 'Curated Resources',
    'resources.subtitle': 'My collection of useful resources and tools',
    'resources.search.placeholder': 'Search resources...',
    'resources.filter.all': 'All',
    'resources.filter.development': 'Development',
    'resources.filter.ai-ml': 'AI/ML',
    'resources.filter.design': 'Design',
    'resources.filter.learning': 'Learning',
    'resources.filter.tools': 'Tools',
    'resources.featured': 'Featured',
    'resources.visit': 'Visit Resource',
    'resources.no-results': 'No resources found',
    'resources.no-results.desc': 'Try adjusting your search terms or filters',
    
    // Contact Section
    'contact.title': 'Let\'s Connect!',
    'contact.subtitle': 'Let\'s work together to create something amazing',
    'contact.description': 'I\'m always excited to collaborate on new projects, discuss innovative ideas, or just have a chat about technology and creativity. Feel free to reach out!',
    'contact.email': 'Email',
    'contact.github': 'GitHub',
    'contact.linkedin': 'LinkedIn',
    'contact.location': 'Location',
    'contact.location.value': 'Paris, France',
    'contact.message.title': 'Send me a message',
    'contact.message.description': 'Have a project in mind? Want to collaborate? Or just want to say hi? I\'d love to hear from you!',
    'contact.form.name': 'Name',
    'contact.form.name.placeholder': 'Your name',
    'contact.form.email': 'Email',
    'contact.form.email.placeholder': 'your.email@example.com',
    'contact.form.subject': 'Subject',
    'contact.form.subject.placeholder': 'What\'s this about?',
    'contact.form.message': 'Message',
    'contact.form.message.placeholder': 'Tell me about your project or just say hello...',
    'contact.form.submit': 'Send Message',
    'contact.form.success': 'Thank you for your message! I\'ll get back to you soon.',
    'contact.footer': 'Made with',
    'contact.footer.and': 'and lots of',
    'contact.footer.by': 'by Nadir',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Something went wrong',
    
    // Legal & GDPR
    'legal.privacy': 'Privacy Policy',
    'legal.terms': 'Terms of Service',
    'legal.cookies': 'Cookie Policy',
    'legal.gdpr': 'GDPR Compliance',
    'legal.copyright': '© 2024 Nadir. All rights reserved.',
    'gdpr.title': 'Privacy & Data Protection',
    'gdpr.description': 'This website respects your privacy and complies with GDPR regulations. Your data is processed securely and transparently.',
    'gdpr.data.controller': 'Data Controller: Nadir',
    'gdpr.data.purpose': 'Purpose: Portfolio and professional communication',
    'gdpr.data.retention': 'Data retention: Contact information is kept for professional communication purposes only',
    'gdpr.rights': 'Your rights: Access, rectification, erasure, portability, objection',
    'gdpr.contact': 'For any data protection inquiries, contact: n.benadda.dev@gmail.com',
    'cookies.title': 'Cookie Usage',
    'cookies.description': 'This website uses essential cookies for functionality. No tracking or analytics cookies are used.',
    'cookies.accept': 'Accept',
    'cookies.decline': 'Decline',
  },
  fr: {
    // Hero Section
    'hero.title': "Bonjour, je suis Nadir",
    'hero.subtitle': 'Développeur Web & Passionné d\'IA',
    'hero.description': 'Je crée de belles expériences web fonctionnelles en utilisant React, Node.js et les technologies de pointe.',
    
    // Navigation
    'nav.about': 'À propos',
    'nav.projects': 'Projets',
    'nav.tools': 'Outils',
    'nav.resources': 'Ressources',
    'nav.contact': 'Contact',
    
    // About Section
    'about.title': 'À propos de moi',
    'about.subtitle': 'Développeur passionné créant des expériences numériques',
    'about.description': 'Je suis un développeur web créatif avec une passion pour créer des solutions innovantes. Avec de l\'expérience en React, Flutter, Node.js et IA/ML, j\'adore transformer les idées en réalité grâce à du code propre et efficace.',
    'about.profile.name': 'Nadir',
    'about.profile.title': 'Développeur Web & Passionné d\'IA',
    'about.profile.description': 'Développeur passionné avec un cœur artistique créatif. J\'aime les couleurs, les designs élégants et les UX originales bien organisées inspirées des jeux vidéo. Je me spécialise dans la création de belles expériences web fonctionnelles en utilisant des technologies modernes comme React, Node.js, et j\'explore le monde passionnant de l\'IA et du machine learning.',
    'about.stats.experience': 'Mois d\'Expérience',
    'about.stats.technologies': 'Technologies',
    'about.stats.projects': 'Projet Majeur',
    'about.stats.creativity': 'Créativité',
    'about.skills.title': 'Compétences & Technologies',
    'about.experience.title': 'Expérience Professionnelle',
    'about.experience.systra.position': 'Stagiaire Développeur Web',
    'about.experience.systra.company': 'SYSTRA',
    'about.experience.systra.description': 'Développement d\'un module web intégré au système ERP propriétaire de l\'entreprise en utilisant TypeScript, React et Node.js avec base de données PostgreSQL.',
    'about.experience.systra.location': 'Paris, France',
    'about.skills.level.beginner': 'Débutant',
    'about.skills.level.intermediate': 'Intermédiaire',
    'about.skills.level.advanced': 'Avancé',
    'about.skills.level.expert': 'Expert',
    
    // Projects Section
    'projects.title': 'Projets en vedette',
    'projects.subtitle': 'Découvrez mes derniers travaux et innovations',
    'projects.alfred.title': 'Alfred - Assistant Culinaire',
    'projects.alfred.description': 'Un assistant culinaire intelligent qui vous aide à créer des plats incroyables avec les ingrédients que vous avez à la maison.',
    'projects.alfred.intro': 'Fini les "qu\'est-ce qu\'on mange ce soir ?" et les courses oubliées ! Alfred transforme votre cuisine en un espace où cuisiner devient un plaisir simple et intuitif.',
    'projects.alfred.features.waste.title': 'Terminé le gaspillage',
    'projects.alfred.features.waste.text': 'Alfred surveille vos aliments, vous alerte avant expiration et suggère des recettes pour utiliser ce que vous avez',
    'projects.alfred.features.stress.title': 'Cuisinez sans stress',
    'projects.alfred.features.stress.text': 'Guidage vocal mains-libres, timers automatiques et conseils personnalisés selon votre niveau',
    'projects.alfred.features.discover.title': 'Découvrez de nouveaux plats',
    'projects.alfred.features.discover.text': 'Recommandations intelligentes basées sur vos goûts, restrictions alimentaires et ce qui traîne dans votre frigo',
    'projects.alfred.features.time.title': 'Gagnez du temps',
    'projects.alfred.features.time.text': 'Scannez vos courses, planifiez vos repas et laissez Alfred organiser votre semaine culinaire',
    'projects.alfred.features.connected.title': 'Une cuisine connectée',
    'projects.alfred.features.connected.text': 'Contrôlez tout à la voix pendant que vous cuisinez, sans jamais toucher votre téléphone',
    'projects.alfred.objective.title': 'L\'objectif',
    'projects.alfred.objective.text': 'Rendre la cuisine accessible à tous, réduire le stress des repas quotidiens et vous faire redécouvrir le plaisir de cuisiner, que vous soyez débutant ou chef confirmé.',
    'projects.meta.started': 'Commencé',
    'projects.meta.status.completed': 'Terminé',
    'projects.meta.status.in-progress': 'En cours',
    'projects.meta.status.planned': 'Planifié',
    'projects.action.live-demo': 'Démo en direct',
    'projects.action.view-code': 'Voir le code',
    'projects.action.app-store': 'App Store',
    'projects.action.google-play': 'Google Play',
    'projects.tech.expand': 'Voir plus',
    'projects.tech.collapse': 'Voir moins',
    'projects.tech.others': 'autres',
    'projects.badge.soon': 'Bientôt',
    'projects.portfolio.title': 'Portfolio Couteau Suisse',
    'projects.portfolio.description': 'Portfolio modulaire avec outils intégrés et système de design personnalisé.',
    'projects.systra.title': 'Module ERP SYSTRA',
    'projects.systra.description': 'Module web intégré au système ERP propriétaire de SYSTRA pour la gestion des projets d\'infrastructure.',
    
    // Tools Section
    'tools.title': 'Outils couteau suisse',
    'tools.subtitle': 'Outils interactifs et utilitaires',
    'tools.recipe.title': 'Générateur de recettes',
    'tools.recipe.description': 'Générez de délicieuses recettes à partir de vos ingrédients disponibles',
    'tools.redthread.title': 'Red Thread',
    'tools.redthread.description': 'Moteur de recommandations de films basé sur vos préférences',
    'tools.comingsoon.title': 'Bientôt disponible',
    'tools.comingsoon.description': 'Cet outil est actuellement en cours de développement',
    'tools.comingsoon.feature.development': 'En développement',
    'tools.comingsoon.feature.development.desc': 'Cet outil est actuellement en cours de développement',
    'tools.comingsoon.feature.available': 'Bientôt disponible',
    'tools.comingsoon.feature.available.desc': 'Recommandations de films personnalisées',
    
    // Resources Section
    'resources.title': 'Ressources utiles',
    'resources.subtitle': 'Ma collection de ressources et outils utiles',
    'resources.search.placeholder': 'Rechercher des ressources...',
    'resources.filter.all': 'Tout',
    'resources.filter.development': 'Développement',
    'resources.filter.ai-ml': 'IA/ML',
    'resources.filter.design': 'Design',
    'resources.filter.learning': 'Apprentissage',
    'resources.filter.tools': 'Outils',
    'resources.featured': 'En vedette',
    'resources.visit': 'Visiter la ressource',
    'resources.no-results': 'Aucune ressource trouvée',
    'resources.no-results.desc': 'Essayez d\'ajuster vos termes de recherche ou filtres',
    
    // Contact Section
    'contact.title': 'Restons en contact !',
    'contact.subtitle': 'Travaillons ensemble pour créer quelque chose d\'incroyable',
    'contact.description': 'Je suis toujours enthousiaste à l\'idée de collaborer sur de nouveaux projets, discuter d\'idées innovantes ou simplement bavarder sur la technologie et la créativité. N\'hésitez pas à me contacter !',
    'contact.email': 'Email',
    'contact.github': 'GitHub',
    'contact.linkedin': 'LinkedIn',
    'contact.location': 'Localisation',
    'contact.location.value': 'Paris, France',
    'contact.message.title': 'Envoyez-moi un message',
    'contact.message.description': 'Vous avez un projet en tête ? Vous voulez collaborer ? Ou vous voulez juste dire bonjour ? J\'aimerais vous entendre !',
    'contact.form.name': 'Nom',
    'contact.form.name.placeholder': 'Votre nom',
    'contact.form.email': 'Email',
    'contact.form.email.placeholder': 'votre.email@exemple.com',
    'contact.form.subject': 'Sujet',
    'contact.form.subject.placeholder': 'De quoi s\'agit-il ?',
    'contact.form.message': 'Message',
    'contact.form.message.placeholder': 'Parlez-moi de votre projet ou dites simplement bonjour...',
    'contact.form.submit': 'Envoyer le message',
    'contact.form.success': 'Merci pour votre message ! Je vous recontacterai bientôt.',
    'contact.footer': 'Fait avec',
    'contact.footer.and': 'et beaucoup de',
    'contact.footer.by': 'par Nadir',
    
    // Common
    'common.loading': 'Chargement...',
    'common.error': 'Quelque chose s\'est mal passé',
    
    // Legal & GDPR
    'legal.privacy': 'Politique de confidentialité',
    'legal.terms': 'Conditions d\'utilisation',
    'legal.cookies': 'Politique des cookies',
    'legal.gdpr': 'Conformité RGPD',
    'legal.copyright': '© 2024. Tous droits réservés.',
    'gdpr.title': 'Confidentialité & Protection des données',
    'gdpr.description': 'Ce site web respecte votre vie privée et est conforme au RGPD. Vos données sont traitées de manière sécurisée et transparente.',
    'gdpr.data.controller': 'Responsable du traitement : Nadir',
    'gdpr.data.purpose': 'Finalité : Portfolio et communication professionnelle',
    'gdpr.data.retention': 'Conservation des données : Les informations de contact sont conservées uniquement à des fins de communication professionnelle',
    'gdpr.rights': 'Vos droits : Accès, rectification, effacement, portabilité, opposition',
    'gdpr.contact': 'Pour toute question sur la protection des données, contactez : n.benadda.dev@gmail.com',
    'cookies.title': 'Utilisation des cookies',
    'cookies.description': 'Ce site web utilise des cookies essentiels pour son fonctionnement. Aucun cookie de suivi ou d\'analyse n\'est utilisé.',
    'cookies.accept': 'Accepter',
    'cookies.decline': 'Refuser',
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Détecter la langue du navigateur au démarrage
  const getBrowserLanguage = (): Language => {
    const browserLang = navigator.language || (navigator as any).userLanguage;
    const languages = navigator.languages || [browserLang];
    
    // Vérifier toutes les langues préférées du navigateur
    for (const lang of languages) {
      const langCode = lang.toLowerCase();
      
      // Langues françaises supportées
      if (langCode.startsWith('fr')) {
        return 'fr';
      }
      
      // Langues anglaises (par défaut)
      if (langCode.startsWith('en')) {
        return 'en';
      }
    }
    
    // Par défaut, utiliser l'anglais
    return 'en';
  };

  const [language, setLanguage] = useState<Language>(() => {
    // Vérifier d'abord le localStorage pour une préférence sauvegardée
    const savedLanguage = localStorage.getItem('portfolio-language') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'fr')) {
      return savedLanguage;
    }
    
    // Sinon, utiliser la langue du navigateur
    return getBrowserLanguage();
  });

  const handleSetLanguage = (newLanguage: Language) => {
    if (newLanguage === language) return;
    
    console.log('Setting language to:', newLanguage);
    setLanguage(newLanguage);
    
    // Sauvegarder la préférence dans le localStorage
    localStorage.setItem('portfolio-language', newLanguage);
  };

  const t = (key: string): string => {
    try {
      const currentTranslations = translations[language] as Record<string, string>;
      if (!currentTranslations) {
        console.error(`No translations found for language: ${language}`);
        return key;
      }
      
      const translation = currentTranslations[key];
      if (!translation) {
        console.warn(`Translation key "${key}" not found for language "${language}", falling back to English`);
        const englishTranslations = translations['en'] as Record<string, string>;
        return englishTranslations[key] || key;
      }
      
      return translation;
    } catch (error) {
      console.error(`Error in translation function:`, error);
      return key;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
